import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";

/**
 * Contrarian Narrative Engine — detects interesting contradictions in the
 * derived snapshot. Pure deterministic, returns 0..N alerts ranked by
 * shareability.
 */

export type ContrarianKind =
  | "stats_vs_momentum"
  | "resisting_pressure"
  | "possession_without_danger"
  | "underdog_surviving"
  | "false_dominance";

export interface ContrarianAlert {
  id: string;
  kind: ContrarianKind;
  headline: string;
  detail: string;
  shareability: number; // 0..1
}

export interface ContrarianContext {
  homeLabel: string;
  awayLabel: string;
  scoreHome: number;
  scoreAway: number;
}

export function detectContrarianAlerts(
  snap: DerivedSnapshot,
  ctx: ContrarianContext,
): ContrarianAlert[] {
  const out: ContrarianAlert[] = [];
  const w = snap.windows.w5m;
  const s = snap.signals;

  // Possession high but no real danger
  if (w.avgPossessionHome > 0.62 && w.xgHomeAccum < 0.18 && w.dangerCount < 2) {
    out.push({
      id: "poss-no-danger-h",
      kind: "possession_without_danger",
      headline: `${ctx.homeLabel} ${Math.round(w.avgPossessionHome * 100)}% possession — zero danger`,
      detail: "High ball control, but no penetration in the final third.",
      shareability: 0.72,
    });
  } else if (w.avgPossessionHome < 0.38 && w.xgAwayAccum < 0.18 && w.dangerCount < 2) {
    out.push({
      id: "poss-no-danger-a",
      kind: "possession_without_danger",
      headline: `${ctx.awayLabel} ${Math.round((1 - w.avgPossessionHome) * 100)}% possession — zero danger`,
      detail: "Plenty of ball, no real threat.",
      shareability: 0.72,
    });
  }

  // Stats vs momentum: dominant in stats but losing momentum
  if (w.avgPossessionHome > 0.58 && s.momentumShift < -0.35) {
    out.push({
      id: "stats-vs-mom-h",
      kind: "stats_vs_momentum",
      headline: `${ctx.homeLabel} dominates stats but loses momentum`,
      detail: "Possession on the board, but the game is slipping.",
      shareability: 0.85,
    });
  }

  // Resisting extreme pressure (defending side keeps the score)
  if (s.pressureExtreme) {
    const attackerIsHome = w.avgPressureHome > w.avgPressureAway;
    const defenderLabel = attackerIsHome ? ctx.awayLabel : ctx.homeLabel;
    const defenderGoalsAgainst = attackerIsHome
      ? Math.max(w.xgHomeAccum, 0)
      : Math.max(w.xgAwayAccum, 0);
    if (defenderGoalsAgainst < 0.35) {
      out.push({
        id: "resist-press",
        kind: "resisting_pressure",
        headline: `${defenderLabel} resists extreme pressure`,
        detail: "Defensive block holding under an attacking avalanche.",
        shareability: 0.88,
      });
    }
  }

  // Underdog surviving (losing on scoreboard, defending, minute > 60)
  const losingSide =
    ctx.scoreHome < ctx.scoreAway
      ? "home"
      : ctx.scoreAway < ctx.scoreHome
        ? "away"
        : null;
  if (losingSide && snap.minute >= 60) {
    const losingLabel = losingSide === "home" ? ctx.homeLabel : ctx.awayLabel;
    const losingDefRisk =
      losingSide === "home" ? s.defensiveRiskHome : s.defensiveRiskAway;
    if (losingDefRisk < 0.55) {
      out.push({
        id: "underdog",
        kind: "underdog_surviving",
        headline: `${losingLabel} keeps the door shut despite the deficit`,
        detail: "Losing on the scoreboard but defensively organized.",
        shareability: 0.78,
      });
    }
  }

  // False dominance — territorial dominance but xG nearly zero
  if (Math.abs(s.territorialDominance) > 0.4) {
    const homeDom = s.territorialDominance > 0;
    const xgOfDom = homeDom ? w.xgHomeAccum : w.xgAwayAccum;
    if (xgOfDom < 0.15) {
      const label = homeDom ? ctx.homeLabel : ctx.awayLabel;
      out.push({
        id: "false-dom",
        kind: "false_dominance",
        headline: `${label} controls territory but creates nothing`,
        detail: "Territorial dominance without translating into chances.",
        shareability: 0.7,
      });
    }
  }

  return out.sort((a, b) => b.shareability - a.shareability);
}